<template>
  <tr class="bg-gray-50 hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-12 last-child:pr-3 text-sm">
    <td class="py-3 text-left">
      <div class="flex items-center space-x-2">
        <button
          type="button"
          class="btn-icon btn-icon-default"
          data-test="expand-dayworks-records"
          @click.stop="toggleRecords"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 transition-all duration-200"
            :class="[{'rotate-90': expanded}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <span
          v-if="type === DayworksType.plant"
          class="max-w-20 font-semibold text-gray-900"
        >{{ code }}</span>

        <p class="w-60 text-gray-700">
          {{ description }}
        </p>
      </div>
    </td>

    <td class="pr-3">
      {{ $tcc(calculatedAmount) }}
    </td>

    <td v-if="claim.boq_type === 'subcontractor_schedule_of_rates'" />

    <td
      v-if="!falsey(dayworksCost?.current_claim) && !typingClaim && canEditClaim"
      class="bg-green-200/50 cursor-pointer"
      :class="{ 'text-red-600': claimExceedsCalculated }"
      @click="typeClaim"
    >
      <div class="group flex items-center justify-end">
        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-red inline-block invisible"
          :aria-label="$t('Subcontract.SubcontractorClaimShow.RatesTradeRowItem.accessibility.removeClaim')"
          @click.stop.capture="removeClaim"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>
        {{ $tcc(dayworksCost.current_claim) }}
      </div>
    </td>

    <td
      v-else-if="canEditClaim"
      class="bg-green-200/50"
      :class="{ 'text-red-600': claimExceedsCalculated }"
    >
      <div class="flex items-center justify-end space-x-1">
        <AppCurrencyInput
          ref="claimInput"
          v-model.number="costForm.form.current_claim"
          name="current_claim"
          type="number"
          class="w-20 h-6 ml-auto form-input"
          :class="{ 'text-red-600': claimExceedsCalculated }"
          @blur="stopTypingClaim"
          @keyup.enter="stopTypingClaim"
          @keyup.esc="typingClaim = false"
        />

        <button
          v-if="calculatedAmount !== 0"
          type="button"
          class="btn-icon btn-icon-green"
          title="Prefill claim amount"
          data-test="prefill-dayworks-claim"
          @mousedown.prevent
          @click.stop="prefillClaim"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
        </button>
      </div>
    </td>

    <td
      v-else
      class="bg-green-200/50"
      :class="{ 'text-red-600': claimExceedsCalculated }"
    >
      {{ !falsey(dayworksCost?.current_claim) ? $tcc(dayworksCost.current_claim) : '' }}
    </td>

    <td
      v-if="!falsey(dayworksCost?.current_certified) && !typingCertified && canEditCertifiedLocal"
      class="bg-green-200/50 cursor-pointer"
      :class="{ 'text-red-600': certifiedExceedsCalculated }"
      @click="typeCertified"
    >
      <div class="group flex items-center justify-end">
        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-red inline-block invisible"
          :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.removeCertified')"
          @click.stop.capture="removeCertified"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <button
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>
        {{ $tcc(dayworksCost.current_certified) }}
      </div>
    </td>

    <td
      v-else-if="canEditCertifiedLocal"
      class="bg-green-200/50"
      :class="{ 'text-red-600': certifiedExceedsCalculated }"
    >
      <div class="flex items-center justify-end space-x-1">
        <AppCurrencyInput
          ref="certifiedInput"
          v-model.number="costForm.form.current_certified"
          name="current_certified"
          type="number"
          class="w-20 h-6 ml-auto form-input"
          :class="{ 'text-red-600': certifiedExceedsCalculated }"
          @blur="stopTypingCertified"
          @keyup.enter="stopTypingCertified"
          @keyup.esc="typingCertified = false"
        />

        <button
          v-if="calculatedAmount !== 0"
          type="button"
          class="btn-icon btn-icon-green"
          title="Prefill certified amount"
          data-test="prefill-dayworks-certified"
          @mousedown.prevent
          @click.stop="prefillCertified"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
        </button>
      </div>
    </td>

    <td
      v-else
      class="bg-green-200/50"
      :class="{ 'text-red-600': certifiedExceedsCalculated }"
    >
      {{ !falsey(dayworksCost?.current_certified) ? $tcc(dayworksCost.current_certified) : '' }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="group bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 && varianceComment?.body ? varianceComment.body : ''"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          aria-label="Edit variance comment"
          @click.stop="editVarianceComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ hasVariance ? $tcc(variance) : '' }}
        </div>
      </div>
    </td>

    <td class="bg-green-200/50">
      {{ $tcc(previousAmount) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ $tcc(previousAmount + (dayworksCost?.current_certified || 0)) }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      colspan="3"
    />

    <td class="bg-blue-100/50 pr-3" />

    <td />

    <td class="bg-orange-200/30 relative z-10" />

    <td
      v-if="showForecasts"
      class="bg-orange-200/30 relative z-10 pr-3"
    />
  </tr>

  <tr
    v-if="expanded"
    class="bg-white"
  >
    <td
      colspan="100%"
      class="px-6 py-3"
    >
      <div
        v-if="loadingRecords"
        class="flex items-center justify-center space-x-2 text-sm text-gray-500 py-2 w-full"
      >
        <AppSpinner />
      </div>

      <template v-else>
        <div
          v-if="records.length"
          class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-y-auto max-h-[320px]"
        >
          <table class="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr class="child:border-b child:border-gray-200 bg-gray-50 sticky top-0 child:py-2.5 child:px-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                <th>Date</th>

                <th class="text-right">
                  Hours
                </th>

                <th class="text-right">
                  Rate
                </th>

                <th class="text-right">
                  Cost
                </th>

                <th>WBS</th>

                <th>Area</th>

                <th>Element</th>

                <th>Activity</th>

                <th>Approved By</th>

                <th>Approved At</th>

                <th class="text-right">
                  Paid
                </th>

                <th>Status</th>
              </tr>
            </thead>

            <tbody class="bg-white divide-y divide-gray-100">
              <tr
                v-for="record in records"
                :key="record.id"
                class="child:p-2"
                :class="{ 'bg-amber-50': !record.$claimable && record.$status !== 'Claimed' }"
              >
                <td>{{ record.day }}</td>

                <td class="text-right">
                  {{ record.hours }}
                </td>

                <td class="text-right">
                  {{ $tcc(record.accrual_rate) }}
                </td>

                <td class="text-right">
                  {{ record.$cost != null ? $tcc(record.$cost) : '' }}
                </td>

                <td>{{ record.wbsCode?.code }}</td>

                <td>{{ record.area }}</td>

                <td>{{ record.element }}</td>

                <td>{{ record.activity }}</td>

                <td>{{ record.user?.name }}</td>

                <td>{{ record.approvedAt?.toLocaleString() }}</td>

                <td class="text-right">
                  {{ record.paid != null ? $tcc(record.paid) : '' }}
                </td>

                <td>
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="statusBadgeClass(record.$status)"
                  >
                    {{ record.$status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="bg-white rounded-lg border border-gray-200 p-4 text-center text-sm text-gray-500"
        >
          No records for this period.
        </div>

        <div
          v-if="hasMorePages"
          class="mt-2"
        >
          <button
            type="button"
            class="btn btn--secondary-blue text-xs"
            :disabled="loadingMore"
            @click="loadMore"
          >
            {{ loadingMore ? 'Loading...' : 'Load more' }}
          </button>
        </div>
      </template>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, falsey } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import { DayworksType } from '@/models/DayworksCost';
import Labour from '@/models/Labour';
import Plant from '@/models/Plant';
import { useCommentsStore } from '@/store/comments';
import { useDayworksCostsStore } from '@/store/dayworksCosts';
import { useLabourDailyRecordStore } from '@/store/labourDailyRecord';
import { usePlantDailyRecordsStore } from '@/store/plantDailyRecord';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = withDefaults(
  defineProps<{
    type: ObjectValues<typeof DayworksType>;
    labour?: Labour;
    plant?: Plant;
    claim: Claim;
    canEditCertified: boolean;
    liteMode?: boolean;
    projectId?: string;
    showPercentages?: boolean;
    showForecasts?: boolean;
  }>(),
  {
    liteMode: false,
  },
);

const dayworksCostsStore = useDayworksCostsStore();
const commentsStore = useCommentsStore();
const item = computed(() => props.type === DayworksType.labour ? props.labour : props.plant);
const itemId = computed(() => item.value?.id);

const dayworksCost = computed(() => {
  if(!itemId.value) {
    return undefined;
  }

  const query = dayworksCostsStore.models.with('comments').where('claim_id', props.claim.id);

  if(props.type === DayworksType.labour) {
    return query.where('labour_id', itemId.value).first();
  }

  return query.where('plant_id', itemId.value).first();
});

const description = computed(() => {
  if(props.type === DayworksType.labour) {
    return `${props.labour?.first_name ?? ''} ${props.labour?.last_name ?? ''}`.trim();
  }

  return props.plant?.description ?? '';
});

const code = computed(() => {
  if(props.type === DayworksType.labour) {
    return 'Labour';
  }

  return props.plant?.fleet ?? 'Plant';
});

const calculatedAmount = computed(() => {
  if(item.value?.$claim_id === props.claim.id) {
    return item.value.calculatedAmount;
  }

  return 0;
});

const previousAmount = computed(() => {
  if(item.value?.$claim_id === props.claim.id) {
    return item.value.$previous || 0;
  }

  return dayworksCost.value?.$previous || 0;
});

const canEditClaim = computed(() => {
  return props.liteMode && props.claim.status === ClaimStatus.draft;
});

const canEditCertifiedLocal = computed(() => props.canEditCertified);
const typingClaim = ref(falsey(dayworksCost.value?.current_claim));
const typingCertified = ref(falsey(dayworksCost.value?.current_certified));
const claimInput = useTemplateRef('claimInput');
const certifiedInput = useTemplateRef('certifiedInput');
const createDayworksCostAction = useStoreApiAction(dayworksCostsStore.createDayworksCost);
const deleteDayworksCostAction = useStoreApiAction(dayworksCostsStore.deleteDayworksCost);
const updateDayworksCostAction = useStoreApiAction(dayworksCostsStore.updateDayworksCost);
const saveSubcontractorCertifiedAction = useStoreApiAction(dayworksCostsStore.saveSubcontractorCertified);
const fetchRecordsAction = useStoreApiAction(dayworksCostsStore.fetchRecords);
const updateCommentAction = useStoreApiAction(commentsStore.updateComment);

const costForm = useFormData({
  current_certified: undefined as number | undefined,
  current_claim: undefined as number | undefined,
  comment: undefined as string | undefined,
  type: props.type,
  labour_id: props.labour?.id,
  plant_id: props.plant?.id,
});

watch(
  [dayworksCost, calculatedAmount],
  ([newValue]) => {
    if(newValue) {
      costForm.setData(newValue);
    }

    // Prefill from calculated amount when there is no saved value yet.
    if(
      (falsey(costForm.form.current_claim) || costForm.form.current_claim === 0) &&
      calculatedAmount.value !== 0
    ) {
      costForm.form.current_claim = calculatedAmount.value;
    }

    if(
      (falsey(costForm.form.current_certified) || costForm.form.current_certified === 0) &&
      calculatedAmount.value !== 0
    ) {
      costForm.form.current_certified = calculatedAmount.value;
    }
  },
  { immediate: true },
);

const varianceComment = computed(() => {
  return dayworksCost.value?.comments?.[0];
});

const variance = computed(() => {
  return (dayworksCost.value?.current_claim || 0) - (dayworksCost.value?.current_certified || 0);
});

const hasVariance = computed(() => {
  return !falsey(dayworksCost.value?.current_claim) && !falsey(dayworksCost.value?.current_certified);
});

const showCommentButton = computed(() => {
  return hasVariance.value && variance.value !== 0 && !!varianceComment.value && canEditCertifiedLocal.value;
});

const exceedsCalculated = (amount: number | undefined | null) => {
  if(falsey(amount)) {
    return false;
  }

  return (amount as number) - calculatedAmount.value > 0.01;
};

const claimExceedsCalculated = computed(() => {
  const amount = typingClaim.value ?
    costForm.form.current_claim :
    dayworksCost.value?.current_claim;

  return exceedsCalculated(amount);
});

const certifiedExceedsCalculated = computed(() => {
  const amount = typingCertified.value ?
    costForm.form.current_certified :
    dayworksCost.value?.current_certified;

  return exceedsCalculated(amount);
});

const warnAmountExceedsCalculated = async () => {
  await Swal.fire({
    icon: 'warning',
    title: 'Amount exceeds calculated.',
    text: `The amount cannot be greater than the calculated dayworks total of ${
      australianCurrency(calculatedAmount.value)
    }.`,
  });
};

const promptVarianceReason = async () => {
  const comment = await Swal.fire({
    icon: 'warning',
    title: t('HeadContract.ProgressClaimsLite.TradeRowItem.dialog.reasonForAdjustment'),
    text: t('HeadContract.ProgressClaimsLite.TradeRowItem.dialog.varianceReasonText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
  });

  costForm.form.comment = comment?.value;
};

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case 'Claimed':

    case 'Claimed on later claim':
      return 'bg-blue-100 text-blue-800';

    case 'Claimable':
      return 'bg-green-100 text-green-800';

    default:
      return 'bg-amber-100 text-amber-800';
  }
};

const typeClaim = () => {
  if(!canEditClaim.value) {
    return;
  }

  typingClaim.value = true;

  if(dayworksCost.value) {
    costForm.form.current_claim = dayworksCost.value.current_claim;
  }

  nextTick(() => claimInput.value?.focusInput());
};

const typeCertified = () => {
  if(!canEditCertifiedLocal.value) {
    return;
  }

  typingCertified.value = true;

  if(dayworksCost.value) {
    costForm.form.current_certified = dayworksCost.value.current_certified;
  }

  nextTick(() => certifiedInput.value?.focusInput());
};

const prefillClaim = async () => {
  costForm.form.current_claim = calculatedAmount.value;
  await stopTypingClaim();
};

const prefillCertified = async () => {
  costForm.form.current_certified = calculatedAmount.value;
  await stopTypingCertified();
};

const stopTypingClaim = async () => {
  if(
    (falsey(costForm.form.current_claim) && !dayworksCost.value) ||
    !typingClaim.value
  ) {
    return;
  }

  if(exceedsCalculated(costForm.form.current_claim)) {
    await warnAmountExceedsCalculated();

    costForm.form.current_claim = dayworksCost.value?.current_claim ??
      (calculatedAmount.value !== 0 ? calculatedAmount.value : undefined);

    typingClaim.value = true;

    return;
  }

  if(costForm.form.current_claim === dayworksCost.value?.current_claim) {
    typingClaim.value = falsey(dayworksCost.value?.current_claim);

    return;
  }

  if(falsey(costForm.form.current_claim) && dayworksCost.value && falsey(dayworksCost.value.current_certified)) {
    deleteDayworksCostAction.request(dayworksCost.value.id).then(() => {
      typingClaim.value = true;
    }).catch((_error) => {
      typingClaim.value = false;
    });
  } else if(dayworksCost.value) {
    costForm.resetData(['comment']);

    if(
      !falsey(dayworksCost.value.current_certified) &&
      costForm.form.current_claim !== dayworksCost.value.current_certified
    ) {
      await promptVarianceReason();
    }

    updateDayworksCostAction.request(dayworksCost.value.id, {
      current_claim: costForm.form.current_claim,
      comment: costForm.form.comment,
    }).then((_dayworksCost) => {
      if(falsey(dayworksCost.value?.current_claim)) {
        typingClaim.value = true;
      } else {
        typingClaim.value = false;
      }
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidData'),
          text: error.data.message,
        });
      } else {
        toast.error('Failed to save dayworks claim amount');
      }

      if(dayworksCost.value?.current_claim === 0) {
        costForm.form.current_claim = calculatedAmount.value !== 0 ? calculatedAmount.value : undefined;
      } else {
        costForm.form.current_claim = dayworksCost.value?.current_claim;
      }

      typingClaim.value = true;
    });
  } else if(props.liteMode) {
    createDayworksCostAction.request(
      props.claim.id,
      costForm.only(['current_claim', 'comment', 'type', 'labour_id', 'plant_id']),
    ).then((_dayworksCost) => {
      typingClaim.value = false;
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidAmount'),
          text: error.data.message,
        });
      } else {
        toast.error('Failed to save dayworks claim amount');
      }

      typingClaim.value = true;
    });
  }
};

const stopTypingCertified = async () => {
  if(
    (falsey(costForm.form.current_certified) && !dayworksCost.value) ||
    !typingCertified.value
  ) {
    return;
  }

  if(exceedsCalculated(costForm.form.current_certified)) {
    await warnAmountExceedsCalculated();

    costForm.form.current_certified = dayworksCost.value?.current_certified ??
      (calculatedAmount.value !== 0 ? calculatedAmount.value : undefined);

    typingCertified.value = true;

    return;
  }

  if(costForm.form.current_certified === dayworksCost.value?.current_certified) {
    typingCertified.value = falsey(dayworksCost.value?.current_certified);

    return;
  }

  if(props.liteMode && props.claim.status === ClaimStatus.draft) {
    if(falsey(costForm.form.current_certified) && dayworksCost.value && falsey(dayworksCost.value.current_claim)) {
      deleteDayworksCostAction.request(dayworksCost.value.id).then(() => {
        typingCertified.value = true;
      }).catch((_error) => {
        typingCertified.value = false;
      });
    } else if(dayworksCost.value) {
      costForm.resetData(['comment']);

      if(
        !falsey(dayworksCost.value.current_claim) &&
        costForm.form.current_certified !== dayworksCost.value.current_claim
      ) {
        await promptVarianceReason();
      }

      updateDayworksCostAction.request(dayworksCost.value.id, {
        current_certified: costForm.form.current_certified,
        comment: costForm.form.comment,
      }).then((_dayworksCost) => {
        if(falsey(dayworksCost.value?.current_certified)) {
          typingCertified.value = true;
        } else {
          typingCertified.value = false;
        }
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            icon: 'error',
            title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidData'),
            text: error.data.message,
          });
        } else {
          toast.error('Failed to save dayworks certified amount');
        }

        if(dayworksCost.value?.current_certified === 0) {
          costForm.form.current_certified = calculatedAmount.value !== 0 ? calculatedAmount.value : undefined;
        } else {
          costForm.form.current_certified = dayworksCost.value?.current_certified;
        }

        typingCertified.value = true;
      });
    } else {
      createDayworksCostAction.request(
        props.claim.id,
        costForm.only(['current_certified', 'comment', 'type', 'labour_id', 'plant_id']),
      ).then((_dayworksCost) => {
        typingCertified.value = false;
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            icon: 'error',
            title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidAmount'),
            text: error.data.message,
          });
        } else {
          toast.error('Failed to save dayworks certified amount');
        }

        typingCertified.value = true;
      });
    }
  } else if(props.projectId && dayworksCost.value) {
    costForm.resetData(['comment']);

    if(
      !falsey(dayworksCost.value.current_claim) &&
      costForm.form.current_certified !== dayworksCost.value.current_claim
    ) {
      await promptVarianceReason();
    }

    saveSubcontractorCertifiedAction.request(
      props.projectId,
      props.claim.id,
      dayworksCost.value.id,
      {
        current_certified: costForm.form.current_certified,
        comment: costForm.form.comment,
      },
    ).then((_dayworksCost) => {
      if(falsey(dayworksCost.value?.current_certified)) {
        typingCertified.value = true;
      } else {
        typingCertified.value = false;
      }
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidData'),
          text: error.data.message,
        });
      } else {
        toast.error('Failed to save dayworks certified amount');
      }

      costForm.form.current_certified = dayworksCost.value?.current_certified;
      typingCertified.value = true;
    });
  }
};

const deleteCost = () => {
  if(!dayworksCost.value) {
    return;
  }

  deleteDayworksCostAction.request(dayworksCost.value.id).then(() => {
    if(calculatedAmount.value !== 0) {
      costForm.form.current_claim = calculatedAmount.value;
      costForm.form.current_certified = calculatedAmount.value;
    } else {
      costForm.form.current_claim = undefined;
      costForm.form.current_certified = undefined;
    }

    typingCertified.value = true;
    typingClaim.value = true;
  });
};

const removeClaim = () => {
  if(!dayworksCost.value) {
    return;
  }

  if(falsey(dayworksCost.value.current_certified)) {
    deleteCost();
  } else {
    updateDayworksCostAction.request(dayworksCost.value.id, { current_claim: undefined })
      .then((_dayworksCost) => {
        typingClaim.value = true;

        if(calculatedAmount.value !== 0) {
          costForm.form.current_claim = calculatedAmount.value;
        }
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            icon: 'error',
            title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidAmount'),
            text: error.data.message,
          });
        }

        costForm.form.current_claim = dayworksCost.value?.current_claim;
      });
  }
};

const removeCertified = () => {
  if(!dayworksCost.value) {
    return;
  }

  if(falsey(dayworksCost.value.current_claim)) {
    deleteCost();
  } else {
    updateDayworksCostAction.request(dayworksCost.value.id, { current_certified: undefined })
      .then((_dayworksCost) => {
        typingCertified.value = true;

        if(calculatedAmount.value !== 0) {
          costForm.form.current_certified = calculatedAmount.value;
        }
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            icon: 'error',
            title: t('HeadContract.ProgressClaimsLite.TradeRowItem.error.invalidAmount'),
            text: error.data.message,
          });
        }

        costForm.form.current_certified = dayworksCost.value?.current_certified;
      });
  }
};

const editVarianceComment = () => {
  if(!dayworksCost.value || !varianceComment.value) {
    return;
  }

  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentTitle'),
    text: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: 'Update',
  }).then((result) => {
    if(result.isConfirmed && result.value) {
      updateCommentAction.request(varianceComment.value.id, { comment: result.value }).then(() => {
        toast.success('Comment updated');
      });
    }
  });
};

const expanded = ref(false);
const loadingRecords = ref(false);
const loadingMore = ref(false);
const recordIds = ref<number[]>([]);
const currentPage = ref(1);
const lastPage = ref(1);
const hasMorePages = computed(() => currentPage.value < lastPage.value);
const labourDailyRecordsStore = useLabourDailyRecordStore();
const plantDailyRecordsStore = usePlantDailyRecordsStore();

const records = computed(() => {
  if(props.type === DayworksType.labour) {
    return labourDailyRecordsStore.models.query().find(recordIds.value);
  }

  return plantDailyRecordsStore.models.query().find(recordIds.value);
});

const toggleRecords = async () => {
  expanded.value = !expanded.value;

  if(!expanded.value || !props.projectId || !itemId.value) {
    return;
  }

  loadingRecords.value = true;

  try {
    const data = await fetchRecordsAction.request(
      props.projectId,
      props.claim.id,
      props.type,
      itemId.value,
      1,
    );

    recordIds.value = data.data.map((record) => record.id);
    currentPage.value = data.meta.current_page;
    lastPage.value = data.meta.last_page;
  } catch (error) {
    toast.error('Failed to load dayworks records');
    expanded.value = false;
  } finally {
    loadingRecords.value = false;
  }
};

const loadMore = async () => {
  if(!props.projectId || !itemId.value || !hasMorePages.value) {
    return;
  }

  loadingMore.value = true;

  try {
    const nextPage = currentPage.value + 1;

    const data = await fetchRecordsAction.request(
      props.projectId,
      props.claim.id,
      props.type,
      itemId.value,
      nextPage,
    );

    const moreIds = data.data.map((record) => record.id);

    recordIds.value = recordIds.value.concat(moreIds);
    currentPage.value = data.meta.current_page;
    lastPage.value = data.meta.last_page;
  } catch (error) {
    toast.error('Failed to load more records');
  } finally {
    loadingMore.value = false;
  }
};
</script>
